import React, {
    useRef,
    useMemo,
    useCallback,
    useState,
    useEffect
} from 'react';
import { KanbanColumn, KanbanRegularColumn } from '../HBKanban.types';
import HBKanbanCard from '../HBKanbanCard/HBKanbanCard';
import HBKanbanInfiniteScroll from '../HBKanbanInfiniteScroll/HBKanbanInfiniteScroll';
import { useDroppable } from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useHBKanbanContext } from '../HBKanbanContext';
import ColumnHeader from './ColumnHeader/ColumnHeader';
import {
    StyledColumn,
    StyledColumnContent,
    DashedBorder,
    DashedBorderRect,
    LoaderContainer,
    StyledColumnItems,
    StyledColumnItemsWrapper
} from './HBKanbanColumn.styles';
import HBLoader from '../../HBLoader/HBLoader';
import HBKanbanDivider from './HBKanbanDivider';
import { useDetectScroll } from '../../../hooks/useDetectScroll';
import { VirtualizedCardList } from './VirtualizedCardList';

/**
 * Internal component that handles regular (non-divider) columns
 * This is separated to ensure hooks are always called in the same order
 */
const HBKanbanRegularColumn = <TItemData = any, TColumnData = any>({
    column,
    className
}: {
    column: KanbanRegularColumn<TColumnData>;
    className?: string;
}) => {
    const columnRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Get all data from context
    const {
        items,
        testIdName,
        infiniteScrollProps: infiniteScroll,
        onLoadMore,
        isLoadingColumn,
        draggingOverNonDroppable,
        registerColumnContentRef,
        registerColumnRef,
        virtualization = { mode: 'auto', threshold: 50 }
    } = useHBKanbanContext<TItemData, TColumnData>();

    // Get items for this column
    const columnItems = useMemo(
        () => items[column.id] || [],
        [column.id, items]
    );

    // Determine if virtualization should be enabled for this column
    const shouldVirtualize = useMemo(() => {
        const mode = virtualization?.mode ?? 'auto';

        if (mode === 'on') {
            return true;
        }
        if (mode === 'off') {
            return false;
        }
        // 'auto' - enable based on threshold
        const threshold =
            virtualization?.mode === 'auto'
                ? virtualization.threshold ?? 50
                : 50;
        return columnItems.length >= threshold;
    }, [virtualization, columnItems.length]);

    const { isOver, setNodeRef, over } = useDroppable({
        id: column.id
    });

    // Check if we're hovering over this column
    const isOverColumn =
        isOver ||
        (over?.id &&
            !!items[column.id]?.find(card => {
                return card.id === over.id;
            }));

    // Track if we're hovering over this non-droppable column
    // draggingOverNonDroppable contains the column ID that is non-droppable (or null)
    // If we're over this column and it matches the non-droppable column ID, show non-droppable styles
    const isOverNonDroppable =
        isOverColumn && draggingOverNonDroppable === column.id;

    // Combine refs
    const combinedRef = useCallback(
        (node: HTMLDivElement | null) => {
            columnRef.current = node;
            setNodeRef(node);
        },
        [setNodeRef]
    );

    const hasMore = infiniteScroll?.hasMore
        ? infiniteScroll.hasMore(column.id)
        : true;

    const isColumnLoading = isLoadingColumn?.(column.id) || false;

    // Handle load more trigger
    const handleLoadMore = useCallback(() => {
        if (!onLoadMore || isColumnLoading) {
            return;
        }
        onLoadMore(column.id, currentPage);
        setCurrentPage(prev => prev + 1);
    }, [onLoadMore, column.id, currentPage, isColumnLoading]);

    const isEmpty = columnItems.length === 0;
    const intent = column.intent;

    // Detect vertical scrolling in the column content
    const { isScrolled: isScrolledTop, isScrolledToEnd: isScrolledToBottom } =
        useDetectScroll({
            elementRef: contentRef,
            direction: 'vertical'
        });

    // Register column content ref for scrolling
    useEffect(() => {
        registerColumnContentRef(column.id, contentRef);
    }, [column.id, registerColumnContentRef]);

    // Register column ref for positioning (e.g., group badges)
    useEffect(() => {
        registerColumnRef(column.id, columnRef);
    }, [column.id, registerColumnRef]);

    // Extract item IDs for SortableContext (needs all IDs, not just visible ones)
    const itemIds = useMemo(
        () => columnItems.map(card => card.id),
        [columnItems]
    );

    return (
        <SortableContext
            id={column.id}
            items={itemIds}
            strategy={verticalListSortingStrategy}
        >
            <StyledColumn
                ref={combinedRef}
                className={className}
                $isOver={isOverColumn}
                $intent={intent}
                $isEmpty={isEmpty}
                data-testid={
                    testIdName ? `${testIdName}-column-${column.id}` : undefined
                }
            >
                <ColumnHeader column={column} headerRef={headerRef} />

                <StyledColumnContent
                    $isOver={isOverColumn}
                    $isOverNonDroppable={isOverNonDroppable}
                    $intent={intent}
                    $isEmpty={isEmpty}
                >
                    {isOverColumn && !isOverNonDroppable && (
                        <DashedBorder>
                            <DashedBorderRect />
                        </DashedBorder>
                    )}
                    <StyledColumnItemsWrapper
                        $isScrolledTop={isScrolledTop}
                        $isScrolledBottom={isScrolledToBottom}
                        $intent={intent}
                    >
                        <StyledColumnItems
                            ref={contentRef}
                            style={
                                shouldVirtualize
                                    ? {
                                          position: 'relative',
                                          display: 'block'
                                      }
                                    : undefined
                            }
                        >
                            {shouldVirtualize ? (
                                <VirtualizedCardList
                                    items={columnItems}
                                    scrollElementRef={contentRef}
                                    infiniteScroll={
                                        infiniteScroll && onLoadMore
                                            ? {
                                                  onLoadMore: handleLoadMore,
                                                  hasMore,
                                                  isLoading: isColumnLoading,
                                                  testIdName,
                                                  columnId: column.id
                                              }
                                            : undefined
                                    }
                                />
                            ) : (
                                <>
                                    {columnItems.map(card => (
                                        <HBKanbanCard
                                            key={card.id}
                                            card={card}
                                        />
                                    ))}
                                    {infiniteScroll && onLoadMore && (
                                        <HBKanbanInfiniteScroll
                                            onLoadMore={handleLoadMore}
                                            hasMore={hasMore}
                                            isLoading={isColumnLoading}
                                            testIdName={testIdName}
                                            columnId={column.id}
                                        >
                                            <LoaderContainer>
                                                <HBLoader
                                                    testId="kanban-load-more--loader"
                                                    size="xs"
                                                />
                                            </LoaderContainer>
                                        </HBKanbanInfiniteScroll>
                                    )}
                                </>
                            )}
                        </StyledColumnItems>
                    </StyledColumnItemsWrapper>
                </StyledColumnContent>
            </StyledColumn>
        </SortableContext>
    );
};

const HBKanbanColumn = <TColumnData = any,>({
    column,
    className
}: {
    column: KanbanColumn<TColumnData>;
    className?: string;
}) => {
    // Handle divider columns
    if (column.type === 'divider') {
        return <HBKanbanDivider column={column} />;
    }

    // Render regular column
    return (
        <HBKanbanRegularColumn
            column={column as KanbanRegularColumn<TColumnData>}
            className={className}
        />
    );
};

export default HBKanbanColumn;
